import React from 'react';
import Icon from 'components/AppIcon';

const MarketReview = ({ marketData, categories, onEdit }) => {
  const getCategoryLabel = (categoryId) => {
    return categories?.find(cat => cat?.id === categoryId)?.label || categoryId;
  };

  const getCategoryIcon = (categoryId) => {
    return categories?.find(cat => cat?.id === categoryId)?.icon || 'HelpCircle';
  };

  const formatDate = (date, time) => {
    if (!date) return 'Not set';
    const dateObj = new Date(`${date}T${time || '23:59'}`);
    return dateObj?.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: time ? '2-digit' : undefined,
      minute: time ? '2-digit' : undefined,
      timeZoneName: 'short'
    });
  };

  const reviewSections = [
    {
      title: 'Market Details',
      editStep: 1,
      icon: 'FileText',
      items: [
        { label: 'Question', value: marketData?.title || 'Not set', type: 'text' },
        { label: 'Description', value: marketData?.description || 'Not set', type: 'textarea' },
        { label: 'Category', value: getCategoryLabel(marketData?.category), type: 'text', icon: getCategoryIcon(marketData?.category) },
        { label: 'Tags', value: marketData?.tags?.length > 0 ? marketData?.tags?.join(', ') : 'None', type: 'tags' },
        { label: 'End Date', value: formatDate(marketData?.endDate, marketData?.endTime), type: 'text' },
        { label: 'Timezone', value: marketData?.timezone, type: 'text' }
      ]
    },
    {
      title: 'Market Settings',
      editStep: 2,
      icon: 'Settings',
      items: [
        { label: 'Minimum Bet', value: `$${marketData?.minBetAmount}`, type: 'currency' },
        { label: 'Maximum Bet', value: `$${marketData?.maxBetAmount}`, type: 'currency' },
        { label: 'Creator Fee', value: `${marketData?.creatorFee}%`, type: 'percentage' },
        { label: 'Resolution Source', value: marketData?.resolutionSource || 'Not specified', type: 'text' },
        { label: 'Resolution Criteria', value: marketData?.resolutionCriteria || 'Not set', type: 'textarea' },
        { label: 'Public Market', value: marketData?.isPublic ? 'Yes' : 'No', type: 'boolean' },
        { label: 'Early Resolution', value: marketData?.allowEarlyResolution ? 'Allowed' : 'Not allowed', type: 'boolean' }
      ]
    }
  ];

  const getValueDisplay = (item) => {
    switch (item?.type) {
      case 'textarea':
        return item?.value?.length > 100 ? `${item?.value?.substring(0, 100)}...` : item?.value;
      case 'tags':
        return item?.value;
      case 'boolean':
        return (
          <span className={`px-2 py-1 rounded text-xs font-body-medium ${
            item?.value === 'Yes' || item?.value === 'Allowed' ?'bg-success-900 bg-opacity-20 text-success' :'bg-surface-hover text-text-secondary'
          }`}>
            {item?.value}
          </span>
        );
      default:
        return item?.value;
    }
  };

  const estimatedEarnings = () => {
    // Simple estimation based on average market volume
    const avgVolume = 10000; // $10k average volume
    const creatorEarnings = (avgVolume * marketData?.creatorFee) / 100;
    return creatorEarnings?.toFixed(2);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-heading-semibold text-text-primary mb-6">Review & Publish</h2>
        <p className="text-text-secondary mb-8">
          Review all market details before publishing. Once published, some settings cannot be changed.
        </p>
      </div>

      {/* Market Preview Card */}
      <div className="bg-surface-hover border-2 border-dashed border-primary-800 rounded-trading p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-primary rounded-full flex items-center justify-center glow-primary">
              <Icon name={getCategoryIcon(marketData?.category)} size={16} color="white" />
            </div>
            <span className="text-xs bg-primary-900 text-primary-300 px-2 py-1 rounded">
              {getCategoryLabel(marketData?.category)}
            </span>
          </div>
          <span className="text-xs text-success flex items-center">
            <Icon name="Clock" size={12} className="mr-1" />
            {formatDate(marketData?.endDate, marketData?.endTime)?.split(',')?.[0]}
          </span>
        </div>

        <h3 className="font-body-semibold text-text-primary mb-2 line-clamp-2">
          {marketData?.title || 'Your market question will appear here'}
        </h3>
        
        <p className="text-sm text-text-secondary mb-4 line-clamp-2">
          {marketData?.description || 'Your market description will appear here'}
        </p>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-surface rounded-trading p-3 text-center">
            <p className="text-xs text-text-secondary mb-1">Yes</p>
            <p className="font-data text-lg text-success">$0.50</p>
          </div>
          <div className="bg-surface rounded-trading p-3 text-center">
            <p className="text-xs text-text-secondary mb-1">No</p>
            <p className="font-data text-lg text-error">$0.50</p>
          </div>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-text-secondary">Volume: $0</span>
          <span className="text-primary">Preview Mode</span>
        </div>
      </div>

      {/* Review Sections */}
      {reviewSections?.map((section, sectionIndex) => (
        <div key={sectionIndex} className="bg-surface border border-border rounded-trading">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center space-x-2">
              <Icon name={section?.icon} size={16} className="text-primary" />
              <h3 className="font-body-semibold text-text-primary">{section?.title}</h3>
            </div>
            <button
              onClick={() => onEdit(section?.editStep)}
              className="flex items-center space-x-1 text-primary hover:text-primary-300 transition-colors duration-trading-fast"
            >
              <Icon name="Edit2" size={14} />
              <span className="text-sm font-body-medium">Edit</span>
            </button>
          </div>
          
          <div className="p-4 space-y-3">
            {section?.items?.map((item, itemIndex) => (
              <div key={itemIndex} className="flex items-start justify-between py-2">
                <div className="flex items-center space-x-2">
                  {item?.icon && <Icon name={item?.icon} size={14} className="text-text-secondary" />}
                  <span className="text-sm font-body-medium text-text-secondary">{item?.label}:</span>
                </div>
                <div className="text-sm text-text-primary text-right max-w-xs">
                  {getValueDisplay(item)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Earnings Estimate */}
      <div className="bg-success-900 bg-opacity-20 border border-success-800 rounded-trading p-4">
        <div className="flex items-start space-x-3">
          <Icon name="DollarSign" size={16} className="text-success mt-0.5" />
          <div>
            <p className="font-body-medium text-sm text-text-primary mb-1">Estimated Earnings</p>
            <p className="text-xs text-text-secondary leading-relaxed">
              With a {marketData?.creatorFee}% creator fee, you could earn approximately 
              <span className="font-data text-success mx-1">${estimatedEarnings()}</span>
              from an average market volume of $10,000. Actual earnings depend on trading activity.
            </p>
          </div>
        </div>
      </div>

      {/* Final Checklist */}
      <div className="bg-surface border border-border rounded-trading p-4">
        <h4 className="font-body-semibold text-text-primary mb-3">Pre-Publishing Checklist</h4>
        <div className="space-y-2">
          {[
            { check: marketData?.title?.length >= 10, text: 'Market question is clear and specific' },
            { check: marketData?.description?.length >= 50, text: 'Detailed description provided' },
            { check: marketData?.category, text: 'Category selected' },
            { check: marketData?.endDate, text: 'End date set' },
            { check: marketData?.resolutionCriteria?.length >= 20, text: 'Resolution criteria defined' }
          ]?.map((item, index) => (
            <div key={index} className="flex items-center space-x-2">
              <Icon 
                name={item?.check ? "CheckCircle" : "Circle"} 
                size={16} 
                className={item?.check ? "text-success" : "text-text-secondary"} 
              />
              <span className={`text-sm ${item?.check ? "text-text-primary" : "text-text-secondary"}`}>
                {item?.text}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Terms Agreement */}
      <div className="bg-surface-hover border border-border rounded-trading p-4">
        <div className="flex items-start space-x-3">
          <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
          <div>
            <p className="font-body-medium text-sm text-text-primary mb-2">Terms & Conditions</p>
            <p className="text-xs text-text-secondary leading-relaxed mb-3">
              By publishing this market, you agree to moderate it fairly and resolve it accurately based on your stated criteria. 
              Markets that violate our guidelines may be removed.
            </p>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary-100 focus:ring-2"
              />
              <span className="text-xs text-text-primary">I agree to the Terms of Service and Market Creator Guidelines</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketReview;